import React, { createContext, useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import { COOKIES } from '../constants';
import processUserAgent from '../utils/processUserAgent';
import setUserCookie from '../utils/setUserCookie';
import useRouteHistory from '../hooks/useRouteHistory';
import useMedia from '../hooks/useMedia';
import useModal from '../hooks/useModal';
import useCookie from '../hooks/useCookie';
import useUserLocation from '../hooks/useUserLocation';
import useNotificationBar from '../hooks/useNotificationBar';
import useError from '../hooks/useError';
import useGeo from '../hooks/useGeo';
import useUser from '../hooks/useUser';
import auth from '../services/authService';
import config from '../../src/client/config';

const {
  token: { key },
} = config;

const Context = createContext({});

const Provider = ({
  children,
  isServer,
  req,
  res,
  asyncData,
  query,
  mediaQueries,
  token,
}) => {
  const serverOnlyContext = () => {
    const {
      headers: { accept, cookie, referer, ...headers },
    } = req;

    const cookies = new Cookies(cookie);
    const user = setUserCookie(query, res);

    return {
      webp: accept && accept.includes('webp'),
      serverCookie: cookie,
      referer,
      userAgent: processUserAgent(headers),
      user: user || cookies.get(COOKIES.LS_USER) || {},
      isServer,
      ...asyncData,
    };
  };

  const rehydrateData = () => {
    // eslint-disable-next-line no-underscore-dangle
    const serverContext = window.__PRELOADED_CONTEXT__;
    if (serverContext) {
      // eslint-disable-next-line no-underscore-dangle
      delete window.__PRELOADED_CONTEXT__;
      return {
        ...serverContext,
      };
    }

    return { userAgent: {} };
  };

  const originalState = isServer ? serverOnlyContext() : rehydrateData();

  const [data, setData] = useState(originalState);
  const routeHistory = useRouteHistory();
  const media = useMedia(mediaQueries, isServer, originalState);
  const modal = useModal();
  const cookies = useCookie(originalState.serverCookie, isServer);
  const notificationBar = useNotificationBar();
  const geo = useGeo(notificationBar);
  const userLocation = useUserLocation(cookies, geo);
  const user = useUser({ token, cookie: cookies });
  const error = useError();

  const setContextData = (name, objData) => {
    setData(currentData => ({
      ...currentData,
      [name]: objData,
    }));
  };

  useEffect(() => {
    setContextData('isServer', false);
  }, []);

  return (
    <Context.Provider
      value={{
        ...data,
        routeHistory,
        media,
        setContextData,
        setData,
        modal,
        cookie: cookies,
        userLocation,
        notificationBar,
        user,
        error,
        auth: {
          ...auth,
          token: () => cookies.getCookie(key),
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export { Provider, Context };
